import React, { useState } from "react";
import { styled } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import MuiAppBar, { AppBarProps as MuiAppBarProps } from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import List from '@mui/material/List';
import IconButton from '@mui/material/IconButton';
import MenuIcon from '@mui/icons-material/Menu';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import Typography from '@mui/material/Typography';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { Grid, Select, MenuItem } from '@mui/material';
import { connect } from "react-redux";
import { ssoSignOut } from "../../actions/AuthActions";
import history from "../../history";
import Avatar from '@mui/material/Avatar';

const drawerWidth = 350;

declare module '@mui/material/styles' {
    interface PaletteColor {
        darker?: string;
    }

    interface SimplePaletteColorOptions {
        darker?: string;
    }
}

const Main = styled('main', { shouldForwardProp: (prop) => prop !== 'open' })<{
    open?: boolean;
}>(({ theme, open }) => ({
    flexGrow: 1,
    padding: theme.spacing(3),
    transition: theme.transitions.create('margin', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    marginLeft: `-${drawerWidth}px`,
    ...(open && {
        transition: theme.transitions.create('margin', {
            easing: theme.transitions.easing.easeOut,
            duration: theme.transitions.duration.enteringScreen,
        }),
        marginLeft: 0,
    }),
}));

interface UserSideBarProps {
    content: any;
    logo: any;
    uuid: string;
    studentFirstName: string;
    studentLastName: string;
    accountType: string;
    ssoSignOut: () => void;
    interviewCount: number;
}

interface AppBarProps extends MuiAppBarProps {
    open?: boolean;
}

const AppBar = styled(MuiAppBar, {
    shouldForwardProp: (prop) => prop !== 'open',
})<AppBarProps>(({ theme, open }) => ({
    transition: theme.transitions.create(['margin', 'width'], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    ...(open && {
        width: `calc(100% - ${drawerWidth}px)`,
        marginLeft: `${drawerWidth}px`,
        transition: theme.transitions.create(['margin', 'width'], {
            easing: theme.transitions.easing.easeOut,
            duration: theme.transitions.duration.enteringScreen,
        }),
    }),
}));

const DrawerHeader = styled('div')(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(0, 1),
    // necessary for content to be below app bar
    ...theme.mixins.toolbar,
    justifyContent: 'flex-end',
}));


export const UserSideBar: React.FC<UserSideBarProps> = ({ content, logo, uuid, studentFirstName, studentLastName, accountType, ssoSignOut, interviewCount }) => {
    const [open, setOpen] = React.useState(true);
    const [selectedItem, setSelectedItem] = useState<string>("verification");
    const listItembuttonBackgroundColor = "#d9e8e1";

    const handleDrawerOpen = () => {
        setOpen(true);
    };

    const handleDrawerClose = () => {
        setOpen(false);
    };

    const handleNavigate = (item: string, path: string) => {
        setSelectedItem(item);
        history.push(path);
    }

    const userFullName = (accountType === "parent" ? "Parent of: " : "") + studentFirstName + " " + studentLastName;

    return (
        <Box sx={{ display: 'flex' }}>
            <AppBar position="fixed" open={open}>
                <Toolbar sx={{ backgroundColor: "white" }}>
                    <Grid container spacing={2}>
                        <Grid item xs={4}>
                            {!open && (
                                <IconButton
                                    aria-label="open drawer"
                                    onClick={handleDrawerOpen}
                                    edge="start"
                                >
                                    <MenuIcon />
                                </IconButton>
                            )}
                        </Grid>
                        <Grid item xs={8}>
                            <div style={{ color: 'rgba(0, 0, 0, 0.54)', float: 'right' }}>
                                <label htmlFor={'logoutSelectID'} id="logoutID"><strong>{userFullName}</strong></label>
                                <Select
                                    labelId="logoutID"
                                    id="logoutSelect"
                                    label="logout"
                                    variant='standard'
                                    disableUnderline
                                    inputProps={{ 'id': 'logoutSelectID' }}
                                    sx={{ boxShadow: 'none', '.MuiOutlinedInput-notchedOutline': { border: 0 } }}
                                    IconComponent={KeyboardArrowDownIcon}
                                >
                                    <MenuItem style={{ minWidth: '100px' }} value={0} onClick={() => ssoSignOut()}>
                                        Log Out
                                    </MenuItem>
                                </Select>
                            </div>
                        </Grid>
                    </Grid>
                </Toolbar>
            </AppBar>
            <Drawer
                sx={{
                    width: drawerWidth,
                    flexShrink: 0,
                    '& .MuiDrawer-paper': {
                        width: drawerWidth,
                        boxSizing: 'border-box',
                    },
                }}
                variant="persistent"
                anchor="left"
                open={open}
            >
                <DrawerHeader>
                    <Grid container spacing={2}>
                        <Grid item xs={9}>
                            <div style={{ float: 'left' }}>{logo}</div>
                        </Grid>
                        <Grid item xs={3}>
                            <div style={{ float: 'right' }}>
                                <IconButton aria-label="closeDrawer" onClick={handleDrawerClose}>
                                    <MenuOpenIcon></MenuOpenIcon>
                                </IconButton>
                            </div>
                        </Grid>
                    </Grid>
                </DrawerHeader>
                <List>
                    <ListItem>
                        <ListItemText color='f5f5f5'>
                            <Typography variant="caption" display="block" gutterBottom>
                                STUDENT DASHBOARD
                            </Typography>
                        </ListItemText>
                    </ListItem>
                    <ListItem disablePadding onClick={() => handleNavigate("verification", `/studentView/${uuid}/studentTaskList`)} secondaryAction={<Avatar sx={{ bgcolor: '#AA1308', width: 24, height: 24 }} variant="circular">
                        <Typography variant="button"> {interviewCount}</Typography>
                    </Avatar>}>
                        <ListItemButton sx={{ '&:hover': { backgroundColor: listItembuttonBackgroundColor }, backgroundColor: (selectedItem === "verification" ? listItembuttonBackgroundColor : "none") }}>
                            <Grid container spacing={2}>
                                <Grid item xs={8}>
                                    <ListItemIcon>
                                        <TaskAltIcon />
                                        <div style={{ paddingLeft: '.5em', paddingTop: '.1em', fontWeight: 'bold' }}>
                                            <Typography variant="body2" display="block">
                                                <strong>Verification</strong>
                                            </Typography>
                                        </div>
                                    </ListItemIcon>
                                </Grid>
                            </Grid>
                        </ListItemButton>
                    </ListItem>
                </List>
                <List>
                    <ListItem>
                        <ListItemText color='f5f5f5'>
                            <Typography variant="caption" display="block" gutterBottom>
                                GENERAL
                            </Typography>
                        </ListItemText>
                    </ListItem>
                    <ListItem disablePadding onClick={() => { handleNavigate("settings", `/settings/${uuid}/studentProfile`) }}>
                        <ListItemButton sx={{ '&:hover': { backgroundColor: listItembuttonBackgroundColor }, backgroundColor: (selectedItem === "settings" ? listItembuttonBackgroundColor : "none") }}>
                            <Grid container spacing={2}>
                                <Grid item xs={8}>
                                    <ListItemIcon>
                                        <SettingsOutlinedIcon />
                                        <div style={{ paddingLeft: '.5em', paddingTop: '.1em' }}>
                                            <Typography variant="body2" display="block">
                                                <strong>Settings</strong>
                                            </Typography>
                                        </div>
                                    </ListItemIcon>
                                </Grid>
                            </Grid>
                        </ListItemButton>
                    </ListItem>
                </List>
            </Drawer>
            <Main open={open}>
                <DrawerHeader />
                {content}
            </Main>
        </Box>
    );
}

const mapStateToProps = (state: any) => {
    return {
        studentFirstName: state.student?.student?.firstName ? state.student.student.firstName : "", //preventing 'undefined'
        studentLastName: state.student?.student?.lastName ? state.student.student.lastName : "", //preventing 'undefined'
        accountType: state.auth.user?.accountType,
        uuid: state.auth.user?.uuid
    };
};

export default connect(mapStateToProps, { ssoSignOut })(
    UserSideBar
);
